import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AudioLines, VolumeX, Waves, CloudRain } from "lucide-react";

// Subtle synthesized spa soundscapes — all generated with WebAudio (no files).
// Each page has a signature mood; the toggle cycles: off -> moods -> off.
const MOODS = ["spa", "ocean", "rain"];
const MOOD_META = {
  spa: { label: "Calm Spa", icon: AudioLines },
  ocean: { label: "Ocean Waves", icon: Waves },
  rain: { label: "Gentle Rain", icon: CloudRain },
};

// Per-page signature mood
const PAGE_MOOD = {
  "/": "spa",
  "/treatments": "spa",
  "/doctors": "rain",
  "/about": "ocean",
  "/gallery": "ocean",
  "/blog": "rain",
  "/contact": "ocean",
};

const noiseBuffer = (ctx, brown = false) => {
  const buf = ctx.createBuffer(1, ctx.sampleRate * 4, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < d.length; i++) {
    const w = Math.random() * 2 - 1;
    if (brown) {
      last = (last + 0.02 * w) / 1.02;
      d[i] = last * 3.2;
    } else d[i] = w;
  }
  return buf;
};

const lfoTo = (ctx, param, freq, depth) => {
  const lfo = ctx.createOscillator();
  lfo.frequency.value = freq;
  const g = ctx.createGain();
  g.gain.value = depth;
  lfo.connect(g).connect(param);
  lfo.start();
  return lfo;
};

// Warm evolving pad — the original signature
const buildSpa = (ctx, master) => {
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 520;
  filter.Q.value = 0.6;
  const lfo = lfoTo(ctx, filter.frequency, 0.07, 240);
  const voices = [
    { freq: 110, gain: 0.05, type: "sine" },
    { freq: 164.81, gain: 0.038, type: "sine" },
    { freq: 220, gain: 0.028, type: "sine" },
    { freq: 329.63, gain: 0.012, type: "triangle" },
  ];
  const oscs = voices.flatMap(({ freq, gain, type }, i) => {
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    o.detune.value = i % 2 === 0 ? 4 : -4;
    const g = ctx.createGain();
    g.gain.value = gain;
    const swell = lfoTo(ctx, g.gain, 0.05 + i * 0.017, gain * 0.45);
    o.connect(g).connect(filter);
    o.start();
    return [o, swell];
  });
  filter.connect(master);
  return oscs;
};

// Rolling waves — brown noise breathing through a slow lowpass
const buildOcean = (ctx, master) => {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, true);
  src.loop = true;
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 480;
  filter.Q.value = 0.8;
  const wave = ctx.createGain();
  wave.gain.value = 0.35;
  const lfo1 = lfoTo(ctx, filter.frequency, 0.06, 300);
  const lfo2 = lfoTo(ctx, wave.gain, 0.09, 0.22);
  src.connect(filter).connect(wave).connect(master);
  src.start();
  return [src, lfo1, lfo2];
};

// Soft rainfall — airy banded noise with a gentle flutter
const buildRain = (ctx, master) => {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx);
  src.loop = true;
  const band = ctx.createBiquadFilter();
  band.type = "bandpass";
  band.frequency.value = 1900;
  band.Q.value = 0.45;
  const low = ctx.createBiquadFilter();
  low.type = "lowpass";
  low.frequency.value = 5600;
  const rain = ctx.createGain();
  rain.gain.value = 0.16;
  const flutter = lfoTo(ctx, rain.gain, 0.35, 0.045);
  src.connect(band).connect(low).connect(rain).connect(master);
  src.start();
  return [src, flutter];
};

const BUILDERS = { spa: buildSpa, ocean: buildOcean, rain: buildRain };

export const AmbientSound = () => {
  const { pathname } = useLocation();
  const [mood, setMood] = useState(null); // null = off
  const rig = useRef(null);
  const generation = useRef(0); // guards against overlapping fade-outs on fast navigation

  const stopRig = () => {
    const r = rig.current;
    rig.current = null;
    if (!r) return;
    r.master.gain.setTargetAtTime(0, r.ctx.currentTime, 0.5);
    const ctxToClose = r.ctx;
    const nodes = r.nodes;
    const gen = generation.current;
    setTimeout(() => {
      if (gen !== generation.current) {
        // a newer rig started meanwhile — still clean this one up quietly
      }
      nodes.forEach((n) => {
        try { n.stop(); } catch {}
      });
      ctxToClose.close();
    }, 1600);
  };

  const startRig = async (m) => {
    generation.current += 1;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") await ctx.resume();
    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    const nodes = BUILDERS[m](ctx, master);
    rig.current = { ctx, master, nodes };
    master.gain.setTargetAtTime(0.5, ctx.currentTime, 1.0);
  };

  const toggle = async () => {
    if (!mood) {
      const m = PAGE_MOOD[pathname] || "spa";
      setMood(m);
      await startRig(m);
      return;
    }
    const next = MOODS[(MOODS.indexOf(mood) + 1) % MOODS.length];
    if (mood === MOODS[MOODS.length - 1]) {
      stopRig();
      setMood(null);
      return;
    }
    stopRig();
    setMood(next);
    await startRig(next);
  };

  // page change while playing -> glide into that page's mood
  useEffect(() => {
    if (!mood) return;
    const target = PAGE_MOOD[pathname] || "spa";
    if (target !== mood) {
      stopRig();
      setMood(target);
      startRig(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => () => stopRig(), []);

  // remember the listener's preference — the brand intro chimes when set
  useEffect(() => {
    try {
      if (mood) localStorage.setItem("rs_sound_pref", mood);
      else localStorage.removeItem("rs_sound_pref");
    } catch {}
  }, [mood]);

  const Icon = mood ? MOOD_META[mood].icon : VolumeX;
  const label = mood ? MOOD_META[mood].label : "Sound off";

  return (
    <div className="fixed bottom-6 left-6 z-40 flex flex-col items-start gap-1.5" data-testid="ambient-sound">
      <motion.button
        onClick={toggle}
        data-testid="ambient-sound-toggle"
        aria-label={mood ? `Soundscape: ${label} — tap for next mood` : "Play a subtle spa soundscape"}
        aria-pressed={!!mood}
        title={mood ? `${label} — tap for next mood` : "Spa soundscape — tap to play"}
        className={`flex h-12 w-12 items-center justify-center rounded-full border backdrop-blur-md transition-colors duration-500 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
          mood
            ? "border-[#D4AF37] bg-[#D4AF37]/90 text-[#0C0A09] shadow-[0_8px_30px_rgba(212,175,55,0.4)]"
            : "border-white/15 bg-black/40 text-[#D4AF37] hover:border-[#D4AF37]/60 hover:bg-black/60"
        }`}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.6, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
      >
        <motion.span
          animate={mood ? { scale: [1, 1.15, 1] } : { scale: 1 }}
          transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
          className="flex"
        >
          <Icon className="h-5 w-5" aria-hidden="true" />
        </motion.span>
      </motion.button>
      <AnimatePresence>
        {mood && (
          <motion.span
            key={mood}
            data-testid="ambient-sound-mood"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            className="rounded-full bg-black/55 px-2.5 py-0.5 text-[9px] font-medium uppercase tracking-[0.2em] text-[#F3E5AB] backdrop-blur-sm"
          >
            {label}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
};
